import {
  ChartDataset,
  ChartStyle
} from "state/ui/editor/elements/ChartElement";
import {
  bandScale,
  datasetDomain,
  defaultPlotArea,
  formatTickLabel,
  generateTicks,
  linearScale,
  seriesColorAt
} from "../chartScales";
import CartesianAxes from "./CartesianAxes";

interface Props {
  dataset: ChartDataset;
  style: ChartStyle;
  width: number;
  height: number;
}

const ColumnChart = ({ dataset, style, width, height }: Props) => {
  const plot = defaultPlotArea(width, height, {
    showLegend: style.showLegend,
    showAxes: style.showAxes,
    fontSize: style.fontSize
  });

  const domain = datasetDomain(dataset);
  const y = linearScale(domain, [plot.top + plot.height, plot.top]);
  const x = bandScale(dataset.headers, [plot.left, plot.left + plot.width]);
  const seriesCount = Math.max(dataset.rows.length, 1);
  const innerBandwidth = x.bandwidth / seriesCount;
  const ticks = generateTicks(domain[0], domain[1]);
  const baseline = y.scale(0);

  return (
    <g>
      <CartesianAxes
        dataset={dataset}
        style={style}
        plot={plot}
        x={x}
        y={y}
        ticks={ticks}
      />

      {dataset.rows.map((row, rowIndex) => {
        const color = seriesColorAt(style.seriesColors, rowIndex);
        return (
          <g key={`series-${rowIndex}`}>
            {row.values.map((value, valueIndex) => {
              if (valueIndex >= dataset.headers.length) return null;
              const bandLeft =
                x.scaleAt(valueIndex) + rowIndex * innerBandwidth;
              const barTop = Math.min(y.scale(value), baseline);
              const barHeight = Math.abs(y.scale(value) - baseline);
              // Negative bars hang below the baseline, so put their label below
              // the bar's bottom edge rather than above the zero line.
              const labelY =
                value < 0 ? barTop + barHeight + style.fontSize : barTop - 4;
              return (
                <g key={`bar-${rowIndex}-${valueIndex}`}>
                  <rect
                    x={bandLeft}
                    y={barTop}
                    width={Math.max(innerBandwidth - 2, 1)}
                    height={Math.max(barHeight, 0)}
                    fill={color}
                  />
                  {style.showDataLabels && (
                    <text
                      x={bandLeft + innerBandwidth / 2}
                      y={labelY}
                      fontSize={style.fontSize}
                      fill={style.color}
                      fontFamily={style.fontFamily}
                      textAnchor="middle"
                    >
                      {formatTickLabel(value)}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}
    </g>
  );
};

export default ColumnChart;
